import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API_BASE_URL, parseSseBlock, resolveAuthorizationHeader, sleep } from "../../../utils/sse";

const SSE_URL = `${API_BASE_URL}/sync/getAllSimplified?sse=true`;
const RETRY_DELAYS_MS = [1000, 2000, 5000, 15000, 30000];
// O servidor limita a vida de cada conexão SSE (ver StreamGetAllSimplifiedSse) para que conexões
// órfãs não vivam para sempre. Fim de stream limpo é rotina, não falha — reconecta com esta pausa
// curta, sem notificar onError e sem entrar no backoff de erro.
const RECONNECT_DELAY_MS = 1000;
// Tempo mínimo de vida para uma conexão ser considerada saudável. O teto do servidor é de 5 min,
// então qualquer stream que morra antes disto encerrou por problema, não por rotina — e precisa
// entrar no backoff. Sem essa distinção, um stream que estabelece e morre em seguida (ex.: exceção
// no servidor depois do primeiro evento) reconecta a cada 1s indefinidamente, porque o caminho de
// fim limpo zera o backoff. Cada reconexão custa a query paginada completa, então isso viraria
// ~5 queries/s por aba — justamente quando o banco já está em dificuldade.
const HEALTHY_CONNECTION_MS = 30000;

export const useGetAllSimplifiedSse = ({ enabled, payload, onSyncs, onError }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const onSyncsRef = useRef(onSyncs);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onSyncsRef.current = onSyncs;
  }, [onSyncs]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const payloadKey = useMemo(
    () => JSON.stringify(payload == null ? {} : payload),
    [payload],
  );

  const refresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    const controller = new AbortController();
    let shouldRun = true;

    const connect = async () => {
      let attempt = 0;
      const body = payloadKey;

      const backoffDelay = () => {
        const delay =
          RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)] || 1000;
        attempt += 1;
        return delay;
      };

      while (shouldRun && !controller.signal.aborted) {
        try {
          const authorization = resolveAuthorizationHeader();
          const headers = {
            Accept: "text/event-stream",
            "Content-Type": "application/json",
          };
          if (authorization) headers.Authorization = authorization;

          const response = await fetch(SSE_URL, {
            method: "POST",
            headers,
            body,
            signal: controller.signal,
          });

          if (!response.ok) {
            throw new Error(`SSE getAllSimplified: HTTP ${response.status}`);
          }

          const reader = response.body?.getReader?.();
          if (!reader) {
            throw new Error("SSE getAllSimplified: sem stream de resposta");
          }

          // O backoff NÃO é zerado só por ter estabelecido a conexão: estabelecer e morrer em
          // seguida é exatamente a patologia que precisa escalar. Zera adiante, se durar.
          const connectedAt = Date.now();
          const decoder = new TextDecoder();
          let buffer = "";

          while (shouldRun && !controller.signal.aborted) {
            const { value, done } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            buffer = buffer.replace(/\r\n/g, "\n");
            const parts = buffer.split("\n\n");
            buffer = parts.pop() || "";

            parts.forEach((block) => {
              const parsed = parseSseBlock(block);
              if (!parsed || parsed.event !== "syncs") return;
              if (!parsed.data) return;
              try {
                const json = JSON.parse(parsed.data);
                if (typeof onSyncsRef.current === "function") {
                  onSyncsRef.current(json);
                }
              } catch (error) {
                if (typeof onErrorRef.current === "function") {
                  onErrorRef.current(error);
                }
              }
            });
          }

          if (!controller.signal.aborted) {
            const healthy = Date.now() - connectedAt >= HEALTHY_CONNECTION_MS;
            if (healthy) attempt = 0;

            try {
              await sleep(healthy ? RECONNECT_DELAY_MS : backoffDelay(), controller.signal);
            } catch {
              return;
            }
            continue;
          }
        } catch (error) {
          if (controller.signal.aborted) return;
          if (typeof onErrorRef.current === "function") {
            onErrorRef.current(error);
          }

          try {
            await sleep(backoffDelay(), controller.signal);
          } catch {
            return;
          }
        }
      }
    };

    connect();

    return () => {
      shouldRun = false;
      controller.abort();
    };
  }, [enabled, payloadKey, refreshKey]);

  return { refresh, payloadKey };
};
